import { useState } from "react"
import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import {
  ArrowRight,
  ArrowUpRight,
  Clock3,
  Download,
  Hand,
  Inbox as InboxIcon,
  Send,
  ShieldCheck,
  MessageCircle,
  Target,
  Users,
  Workflow,
} from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { PageHeading } from "@/components/app/page-heading"
import { downloadCsv } from "@/lib/download"
import { automationsQuery, overviewQuery } from "@/lib/queries"
import { useMe, useWorkspaceContext } from "@/lib/session"
import { cn } from "@/lib/utils"
import "@/styles/overview-polish.css"

export const Route = createFileRoute("/w/$workspace/overview")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(overviewQuery(params.workspace, "7")),
  component: Overview,
})

function greeting(name: string) {
  const hour = new Date().getHours()
  const part = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening"
  return `${part}, ${name.split(" ")[0]}`
}

function duration(seconds: number | null) {
  if (seconds === null) return "—"
  if (seconds < 60) return `${Math.round(seconds)}s`
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m ${s.toString().padStart(2, "0")}s`
}

/** Screen 08, from live counts. */
function Overview() {
  const { workspace } = useWorkspaceContext()
  const me = useMe()
  const [period, setPeriod] = useState<"7" | "30">("7")
  const [series, setSeries] = useState<"all" | "instagram">("all")
  const initial = useSuspenseQuery(overviewQuery(workspace.slug, "7")).data
  const live = useQuery({
    ...overviewQuery(workspace.slug, period),
    placeholderData: (previous) => previous,
  })
  const m = live.data ?? initial
  const journeys = useQuery(automationsQuery(workspace.slug))
  const liveAutomations = journeys.data?.automations.filter(
    (journey) => journey.status === "published" && !journey.blocked
  ).length
  const params = { workspace: workspace.slug }
  const values = m.series.map((d) => (series === "all" ? d.all : d.instagram))
  const max = Math.max(4, ...values)
  const total = values.reduce((a, b) => a + b, 0)
  const delta = m.previousTotalConversations
    ? Math.round(
        ((m.totalConversations - m.previousTotalConversations) / m.previousTotalConversations) * 100
      )
    : null
  const points = values.map(
    (v, i) => `${(i * 700) / Math.max(1, values.length - 1)},${200 - (v / max) * 190}`
  )

  return (
    <div className="page-wrap overview">
      <PageHeading
        title={greeting(me.name)}
        description="Your team’s workload, conversation activity, and next steps."
        eyebrow="Workspace overview"
      >
        <Select value={period} onValueChange={(v) => setPeriod(v as "7" | "30")}>
          <SelectTrigger className="w-36" aria-label="Period">
            <Clock3 className="size-3.5" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          onClick={() =>
            downloadCsv("vienna-overview.csv", [
              ["Metric", "Value"],
              ["Needs a human reply", String(m.needsHumanReply)],
              ["First human response (s)", String(m.firstHumanResponseSeconds ?? "")],
              ["New contacts", String(m.newContacts)],
              ["Live automations", String(liveAutomations ?? "")],
              ["Conversations", String(m.totalConversations)],
            ])
          }
        >
          <Download />
          Export
        </Button>
      </PageHeading>
      <div className="overview-metrics">
        {[
          {
            label: "Needs a human reply",
            value: String(m.needsHumanReply),
            detail: `${m.assignedToMe} assigned to you`,
            icon: InboxIcon,
            to: "/w/$workspace/inbox" as const,
            trend: "View inbox",
          },
          {
            label: "First human response",
            value: duration(m.firstHumanResponseSeconds),
            detail:
              m.firstHumanResponseSeconds === null
                ? "No human replies in this period"
                : "Median, this period",
            icon: Clock3,
            to: "/w/$workspace/reports" as const,
            trend: "View reports",
          },
          {
            label: "New contacts",
            value: m.newContacts.toLocaleString(),
            detail: "From first interactions and imports",
            icon: Users,
            to: "/w/$workspace/contacts" as const,
            trend: "View contacts",
          },
          {
            label: "Live automations",
            value: liveAutomations === undefined ? "—" : String(liveAutomations),
            detail:
              liveAutomations !== undefined
                ? "Published and ready for new runs"
                : journeys.isError
                  ? "Could not load journey activity"
                  : "Loading journey activity…",
            icon: Workflow,
            to: "/w/$workspace/automations" as const,
            trend: "View journeys",
          },
        ].map((card, index) => (
          <Card
            key={card.label}
            className={cn("metric-card", index === 0 && "metric-card-primary")}
          >
            <CardContent>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{card.label}</span>
                <span className="metric-symbol">
                  <card.icon className="size-4" />
                </span>
              </div>
              <div className="metric-value my-4">{card.value}</div>
              <div className="metric-footer">
                <span className="text-[11px] text-muted-foreground">{card.detail}</span>
                <Link
                  to={card.to}
                  params={params}
                  search={{}}
                  className="flex items-center gap-1 text-[10px] font-medium"
                >
                  {card.trend}
                  <ArrowUpRight className="size-3" />
                </Link>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="overview-chart-row">
        <Card className="volume-card">
          <CardHeader className="flex-row items-start justify-between">
            <div>
              <CardTitle>Conversation activity</CardTitle>
              <CardDescription className="mt-1 text-xs">
                New conversations across connected channels.
              </CardDescription>
            </div>
            <Tabs value={series} onValueChange={(v) => setSeries(v as "all" | "instagram")}>
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="instagram">Instagram</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent>
            <div className="mt-1 mb-5 flex items-baseline gap-3">
              <strong className="text-3xl font-semibold tracking-tight">
                {total.toLocaleString()}
              </strong>
              <span className="text-[11px] text-muted-foreground">conversations this period</span>
              {delta !== null && series === "all" && (
                <Badge variant="secondary" className="ml-auto text-[10px]">
                  {delta >= 0 ? "↗" : "↘"} {Math.abs(delta)}%
                </Badge>
              )}
            </div>
            <div
              className="volume-chart"
              role="img"
              aria-label={`Conversation activity, ${total} conversations.`}
            >
              <div className="chart-y">
                {[
                  max,
                  Math.round(max * 0.75),
                  Math.round(max * 0.5),
                  Math.round(max * 0.25),
                  0,
                ].map((v, i) => (
                  <span key={i}>{v}</span>
                ))}
              </div>
              <svg viewBox="0 0 700 200" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="activity-fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--chart-1)" stopOpacity=".24" />
                    <stop offset="100%" stopColor="var(--chart-1)" stopOpacity=".01" />
                  </linearGradient>
                </defs>
                {[0, 50, 100, 150, 199].map((y) => (
                  <line
                    key={y}
                    x1="0"
                    x2="700"
                    y1={y}
                    y2={y}
                    stroke="var(--border)"
                    strokeDasharray="3 5"
                  />
                ))}
                <path
                  d={`M0 200 ${points.map((p) => `L${p}`).join(" ")} L700 200Z`}
                  fill="url(#activity-fill)"
                />
                <polyline
                  points={points.join(" ")}
                  fill="none"
                  stroke="var(--chart-1)"
                  strokeWidth="2.5"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              </svg>
            </div>
            {total === 0 && (
              <p className="overview-chart-empty">
                No new conversations in this period. Try a longer date range or check your connected
                channels.
              </p>
            )}
            <div className="chart-x">
              {m.series
                .filter((_, i) => period === "7" || i % 5 === 0 || i === m.series.length - 1)
                .map((d) => (
                  <span key={d.day}>
                    {new Date(d.day).toLocaleDateString(undefined, {
                      weekday: "short",
                      day: "numeric",
                    })}
                  </span>
                ))}
            </div>
            <div className="overview-chart-legend mt-5 flex gap-5 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-2">
                <i className="size-2 rounded-full bg-chart-1" />
                New conversations
              </span>
              <span>Automation-owned threads are excluded from human workload.</span>
            </div>
          </CardContent>
        </Card>
        <Card className="overview-queue-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Your queue</CardTitle>
              <Badge variant="outline" className="text-[10px]">
                {m.openConversations} open
              </Badge>
            </div>
            <CardDescription className="text-xs">
              The next place you can make a difference.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="queue-list">
              {[
                {
                  icon: InboxIcon,
                  label: "Assigned to you",
                  detail: "Ready for your reply",
                  count: m.assignedToMe,
                  queue: "mine" as const,
                },
                {
                  icon: Users,
                  label: "Unassigned",
                  detail: "Find the right person",
                  count: m.unassigned,
                  queue: "unassigned" as const,
                },
                {
                  icon: Hand,
                  label: "Human handovers",
                  detail: "A journey or teammate needs your help",
                  count: m.handovers,
                  queue: "handover" as const,
                },
              ].map((row) => (
                <Link
                  key={row.label}
                  to={row.queue === "handover" ? "/w/$workspace/handovers" : "/w/$workspace/inbox"}
                  params={params}
                  search={row.queue === "handover" ? {} : { view: row.queue }}
                >
                  <span className="queue-icon">
                    <row.icon size={17} />
                  </span>
                  <span>
                    <strong>{row.label}</strong>
                    <small>{row.detail}</small>
                  </span>
                  <b>{row.count}</b>
                  <ArrowRight className="size-3.5" />
                </Link>
              ))}
            </div>
            <Button className="mt-6 w-full" asChild>
              <Link to="/w/$workspace/inbox" params={params} search={{}}>
                Open inbox
                <ArrowRight />
              </Link>
            </Button>
            <div className="core-hint mt-5">
              <ShieldCheck className="size-4" />
              <span>
                {m.automationOwned
                  ? `Automations are quietly handling another ${m.automationOwned} conversation${m.automationOwned === 1 ? "" : "s"}.`
                  : "No conversations are automation-owned right now."}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
      <div className="overview-bottom-row">
        <Card className="overview-campaign-feature">
          <div className="overview-feature-art" aria-hidden="true">
            <span className="overview-art-label">OUTREACH / CAMPAIGNS</span>
            <div className="campaign-flow-art">
              <span>
                <Target size={21} />
                <small>Audience</small>
              </span>
              <ArrowRight size={16} />
              <span>
                <MessageCircle size={21} />
                <small>Message</small>
              </span>
              <ArrowRight size={16} />
              <span>
                <Send size={21} />
                <small>Delivery</small>
              </span>
            </div>
          </div>
          <CardContent className="overview-feature-copy">
            <span className="overview-eyebrow">Campaigns</span>
            <CardTitle>From audience to conversation.</CardTitle>
            <CardDescription>
              Build your audience, shape your message, and follow each delivery in one place.
            </CardDescription>
            <Button variant="outline" asChild>
              <Link to="/w/$workspace/campaigns" params={params}>
                Explore campaigns <ArrowUpRight />
              </Link>
            </Button>
          </CardContent>
        </Card>
        <Card className="overview-attention-card">
          <CardHeader>
            <span className="overview-attention-symbol" aria-hidden="true">
              <Workflow />
            </span>
            <CardTitle>Every run, accounted for.</CardTitle>
            <CardDescription className="text-xs leading-5">
              Follow each run, review delivery issues, and see where your team can help.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="w-full" asChild>
              <Link to="/w/$workspace/automations/runs" params={params} search={{}}>
                View run history
                <ArrowUpRight />
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
